/**
 * Fila de miembro (AUTONOMY · Layer 1): además de quitar, permite CAMBIAR el
 * rol (diálogo con descripciones) y REASIGNAR la sede — gestión completa de
 * miembros sin desarrollador. El backend protege al último owner.
 */
import { msg } from "@lingui/core/macro";
import { Show } from "solid-js";
import { MapPin, Shield, Trash2, User as UserIcon } from "lucide-solid";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Select } from "@/components/ui/select";
import { useI18n } from "@/providers/i18n-provider";

interface Member {
  id: string;
  role: string;
  campusId?: string | null;
  userId: string;
  user: {
    name: string | null;
    /** D3: `null` = redactado por capacidad, no «sin correo». */
    email?: string | null;
  };
}

interface CampusOption {
  id: string;
  name: string;
}

interface OrganizationMemberRowProps {
  member: Member;
  campuses?: CampusOption[];
  /** ¿El actor puede gestionar miembros (owner/org_admin)? */
  canManage?: boolean;
  /** ¿El actor es owner? (permite ofrecer el rol owner). */
  actorIsOwner?: boolean;
  onRemove: (memberId: string) => void;
  /**
   * Pide abrir el diálogo de cambio de rol para ESTE miembro. El diálogo vive
   * en la PÁGINA (una sola instancia): las filas se re-montan con cada refetch
   * de la query de miembros y un diálogo local moriría al instante.
   */
  onRequestChangeRole?: (member: Member) => void;
  onChangeCampus?: (memberId: string, campusId: string | null) => Promise<void>;
}

const NO_CAMPUS = "__all__";

export function OrganizationMemberRow(props: OrganizationMemberRowProps) {
  const { t } = useI18n();

  const campusName = () =>
    props.campuses?.find((c) => c.id === props.member.campusId)?.name ?? t(msg`All campuses`);

  // D3: `user.email` es `null` cuando el servidor lo redacta por capacidad (un
  // alumno no ve los correos de la academia). El testid y el nombre visible
  // caen al userId para no depender de un dato que puede no venir.
  const handle = () => props.member.user.email ?? props.member.userId;

  const canEditRole = () =>
    Boolean(props.canManage && props.onRequestChangeRole) &&
    // Tocar a un owner exige ser owner (transferencia/degradación).
    (props.member.role !== "owner" || props.actorIsOwner);

  return (
    <div
      class="group flex flex-col gap-2 rounded-lg border border-transparent p-2 hover:bg-muted/50 transition-colors"
      data-testid={`member-row-${handle()}`}
    >
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3 min-w-0">
          <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <UserIcon class="h-4 w-4" />
          </div>
          <div class="min-w-0">
            <p class="truncate text-sm font-medium">{props.member.user.name || handle()}</p>
            <p class="truncate text-xs text-muted-foreground">
              <span class="capitalize">{props.member.role.replace("_", " ")}</span>
              <Show when={props.campuses?.length}>
                {" · "}
                <MapPin class="inline h-3 w-3 align-[-2px]" /> {campusName()}
              </Show>
            </p>
          </div>
        </div>
        <div class="flex items-center gap-1">
          <Show when={canEditRole()}>
            <Button
              variant="ghost"
              size="icon"
              class="h-8 w-8 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label={t(msg`Change role`)}
              data-testid={`member-change-role-${handle()}`}
              onClick={() => props.onRequestChangeRole?.(props.member)}
            >
              <Shield class="h-4 w-4" />
            </Button>
          </Show>
          <Show when={props.member.role !== "owner" && props.canManage !== false}>
            <ConfirmDialog
              title={t(msg`Remove member?`)}
              description={t(msg`Are you sure you want to remove this user from the organization?`)}
              confirmLabel={t(msg`Remove`)}
              variant="destructive"
              onConfirm={() => {
                props.onRemove(props.member.id);
              }}
              renderTrigger={(open) => (
                <Button
                  variant="ghost"
                  size="icon"
                  class="h-8 w-8 text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={open}
                >
                  <Trash2 class="h-4 w-4" />
                </Button>
              )}
            />
          </Show>
        </div>
      </div>

      {/* AUTONOMY: sede del miembro (scoping campus_admin/teacher/staff). */}
      <Show when={props.canManage && props.onChangeCampus && (props.campuses?.length ?? 0) > 0}>
        <div class="pl-12">
          <Select
            aria-label={t(msg`Campus`)}
            value={props.member.campusId ?? NO_CAMPUS}
            onChange={(value) => {
              void props.onChangeCampus?.(props.member.id, value === NO_CAMPUS ? null : value);
            }}
            options={[
              { value: NO_CAMPUS, label: t(msg`All campuses`) },
              ...(props.campuses ?? []).map((c) => ({ value: c.id, label: c.name })),
            ]}
          />
        </div>
      </Show>
    </div>
  );
}
